// Types pour le Module 2: Les Traverses

/**
 * Types de motifs prédéfinis pour les traverses
 */
export enum TypeMotifTraverse {
  SIMPLE = 'simple',
  VAGUE_DOUCE = 'vague_douce',
  LOSANGE = 'losange',
  PERSONNALISE = 'personnalise'
}

/**
 * Types de matériaux pour les traverses
 */
export enum TypeMateriauTraverse {
  FER_A_BETON = 'fer_a_beton',    // Section ronde
  TUBE_CARRE = 'tube_carre'       // Section carrée
}

/**
 * Point 2D pour le canvas de dessin personnalisé
 */
export interface Point2D {
  x: number;
  y: number;
}

/**
 * Courbe de Bézier pour les motifs personnalisés
 */
export interface CourbeBezier {
  id: string;
  debut: Point2D;
  fin: Point2D;
  controle1: Point2D;
  controle2: Point2D;
  selected?: boolean;
}

/**
 * Ligne droite pour les motifs personnalisés
 */
export interface LigneDroite {
  id: string;
  debut: Point2D;
  fin: Point2D;
  selected?: boolean;
}

/**
 * Types d'outils de dessin disponibles
 */
export enum OutilDessin {
  CRAYON = 'crayon',
  LIGNE = 'ligne',
  BEZIER = 'bezier',
  SELECTION = 'selection'
}

/**
 * État de l'outil de sélection
 */
export interface EtatSelection {
  elementSelectionne?: string;
  typeElement?: 'ligne' | 'bezier' | 'crayon';
  poigneeActive?: 'debut' | 'fin' | 'controle1' | 'controle2';
}

/**
 * Motif personnalisé dessiné sur le canvas 2D
 */
export interface MotifPersonnalise {
  id: string;
  nom: string;
  courbes: CourbeBezier[];
  lignes: Point2D[][]; // Chemins de crayon (compatibilité)
  lignesDroites: LigneDroite[]; // Nouvelles lignes droites
  largeur: number;
  hauteur: number;
}

/**
 * Paramètres de configuration des traverses
 */
export interface ParametresTraverses {
  actif: boolean;
  motif: TypeMotifTraverse;
  motifPersonnalise?: MotifPersonnalise;
  nombreRangees: number;
  materiau: TypeMateriauTraverse;
  espacementVertical: number; // en mètres
  hauteurPosition: number; // position verticale par rapport à la base (0-1)
  epaisseur: number; // épaisseur des traverses en mètres
  couleur: string;
}



/**
 * Point 3D pour la géométrie des traverses
 */
export interface Point3D {
  x: number;
  y: number;
  z: number;
}

/**
 * Géométrie d'une traverse individuelle
 */
export interface GeometrieTraverse {
  id: string;
  points: Point3D[];
  indices: number[];
  normales: Point3D[];
  uv: Point2D[];
}

/**
 * Rangée de traverses
 */
export interface RangeeTraverses {
  id: string;
  hauteur: number;
  traverses: GeometrieTraverse[];
  motifApplique: TypeMotifTraverse;
}

/**
 * Structure complète des traverses
 */
export interface StructureTraverses {
  id: string;
  parametres: ParametresTraverses;
  rangees: RangeeTraverses[];
}

/**
 * Configuration du module traverses
 */
export interface TraversesModuleConfig {
  motifsDisponibles: TypeMotifTraverse[];
  couleurDefaut: string;
  materiauDefaut: string;
}

/**
 * Réponse de l'API pour les traverses
 */
export interface TraversesAPIResponse {
  success: boolean;
  data?: StructureTraverses;
  error?: string;
  timestamp: number;
}

/**
 * Événements du module traverses
 */
export interface TraversesEvents {
  onParametresChange: (parametres: ParametresTraverses) => void;
  onMotifChange: (motif: TypeMotifTraverse) => void;
  onMotifPersonnaliseChange: (motif: MotifPersonnalise) => void;
  onGeometrieGenerated: (structure: StructureTraverses) => void;
  onError: (error: string) => void;
}

/**
 * État du module traverses
 */
export interface TraversesState {
  parametres: ParametresTraverses;
  structure?: StructureTraverses;
  isGenerating: boolean;
  error?: string;
  canvasActif: boolean;
  motifEnEdition?: MotifPersonnalise;
}

/**
 * Données nécessaires de la structure de base pour les traverses
 */
export interface DonneesStructureBase {
  forme: string;
  largeur: number;
  longueur: number;
  hauteur: number;
  nombreMontants: number;
  positionsMontants: Point3D[];
  materiau: string; // Matériau de la structure de base
  epaisseur: number; // Épaisseur de la structure de base en mm
  couleur: string; // Couleur globale de la tonnelle
  poutreHaute: {
    points: Point3D[];
    rayon: number;
  };
  poutreBasse: {
    points: Point3D[];
    rayon: number;
  };
  // Ouvertures de portes optionnelles pour exclusion des traverses
  // Chaque porte fournit sa courbe de base sur le périmètre (en XZ) et sa plage verticale [yMin, yMax]
  portes?: { baseCurve: Point3D[]; yMin: number; yMax: number }[];
}

/**
 * Interface pour l'adaptateur de motifs
 */
export interface AdaptateurMotif {
  calculerMotif(
    motif: TypeMotifTraverse,
    donneesBase: DonneesStructureBase,
    parametres: ParametresTraverses
  ): GeometrieTraverse[];
  
  adapterMotifPersonnalise(
    motifPersonnalise: MotifPersonnalise,
    donneesBase: DonneesStructureBase,
    parametres: ParametresTraverses
  ): GeometrieTraverse[];
}